import { useEffect, useRef } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'

/**
 * Payload of the API's "EnrichmentCompleted" SignalR event (ORB-383), broadcast to the user-{userId}
 * group of whoever triggered the re-enrichment. Field names are camelCase to match the API payload.
 */
export interface EnrichmentEvent {
  leadId?: string | null
  fileImportRowId?: string | null
  contactId?: string | null
  campaignId?: string | null
  workspaceId?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  success: boolean
  enrichmentStatus: string
}

/**
 * Subscribes to enrichment-complete events for the current user (joins user-{userId}). Used by the
 * global toast AND by mounted grids / the detail view to refresh the matching row. Ref-counted joins
 * mean multiple subscribers share one group membership. The handler is held in a ref so changing it
 * never re-subscribes.
 */
export function useEnrichmentEvent(handler: (evt: EnrichmentEvent) => void): void {
  const { userProfile } = useAuth()
  const userId = userProfile?.Id
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!userId) return
    const group = `user-${userId}`
    let cancelled = false

    const onEvent = (payload: unknown) => handlerRef.current(payload as EnrichmentEvent)

    signalRService.joinGroup(group).then(() => {
      if (cancelled) {
        signalRService.leaveGroup(group)
        return
      }
      signalRService.on('EnrichmentCompleted', onEvent)
    }).catch(() => { /* connection failures are non-fatal for a notification */ })

    return () => {
      cancelled = true
      signalRService.off('EnrichmentCompleted', onEvent)
      signalRService.leaveGroup(group)
    }
  }, [userId])
}

/** Global enrichment toast — mount once high in the tree so it fires regardless of the current page. */
export function useEnrichmentNotifications(): void {
  const { showSuccess, showError } = useToast()
  useEnrichmentEvent((evt) => {
    const name = [evt.firstName, evt.lastName].filter(Boolean).join(' ').trim() || evt.email || 'Contact'
    if (evt.success) showSuccess('Enriched', `${name} has been enriched`)
    else showError('Enrichment failed', `Enrichment for ${name} didn't finish`)
  })
}
